"use client";

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Headset, ChevronLeft } from "lucide-react";
import { group_by_with_normalize } from "@core/helpers/array";
import Accordion from "@/components/ui/accordion";
import Button from "@/components/ui/button";
import { build_faq_json_ld } from "@/helpers/faqJsonLd";
import JsonLd from "@core/helpers/json-ld-script";

const SCROLL_OFFSET = 96;

function RawIcon({ svg, size = 18, className = "" }) {
    if (!svg) return null;
    return (
        <span
            className={`inline-flex items-center justify-center shrink-0 ${className}`}
            style={{ width: size, height: size }}
            dangerouslySetInnerHTML={{ __html: svg }}
        />
    );
}

export default function FaqPage({ fields }) {
    const groups = useMemo(
        () => group_by_with_normalize(fields?.repeatable_fields, "group"),
        [fields]
    );

    const sections = useMemo(() => {
        if (!groups) return [];

        return Object.entries(groups).map(([groupTitle, items]) => {
            const groupIconSvg = items?.[0]?.icon ?? null;

            return {
                id: groupTitle,
                title: groupTitle,
                iconSvg: groupIconSvg,
                items: (items ?? []).map((item) => ({
                    id: item.id,
                    question: item.question,
                    answerHtml: item.answer,
                    iconSvg: item.icon,
                })),
            };
        });
    }, [groups]);

    const json_ld = useMemo(
        () =>
            build_faq_json_ld(
                sections.flatMap((section) =>
                    section.items.map((item) => ({ question: item.question, answer: item.answerHtml }))
                )
            ),
        [sections]
    );

    const [activeId, setActiveId] = useState(null);
    const [openItemId, setOpenItemId] = useState(null);
    const sectionRefs = useRef({});
    const isClickScrolling = useRef(false);
    const clickTimeoutRef = useRef(null);
    const mobileTabsRef = useRef(null);

    useEffect(() => {
        if (sections.length > 0 && !activeId) {
            setActiveId(sections[0].id);
        }
    }, [sections, activeId]);

    const registerSectionRef = useCallback(
        (id) => (node) => {
            if (node) sectionRefs.current[id] = node;
        },
        []
    );

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (isClickScrolling.current) return;

                const visible = entries
                    .filter((entry) => entry.isIntersecting)
                    .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

                if (visible[0]) {
                    setActiveId(visible[0].target.dataset.sectionId);
                }
            },
            { rootMargin: `-${SCROLL_OFFSET}px 0px -55% 0px`, threshold: [0, 0.25, 0.5, 1] }
        );

        const nodes = Object.values(sectionRefs.current);
        nodes.forEach((node) => node && observer.observe(node));

        return () => observer.disconnect();
    }, [sections]);

    const getScrollOffset = useCallback(() => {
        const header_height =
            parseFloat(
                getComputedStyle(document.documentElement).getPropertyValue("--header-height")
            ) || 0;

        const tabs_bar = mobileTabsRef.current;
        const tabs_height =
            tabs_bar && tabs_bar.offsetParent !== null ? tabs_bar.offsetHeight : 0;

        return header_height + tabs_height + 8;
    }, []);


    const settleScroll = useCallback((node, attempt = 0) => {
        const delta = node.getBoundingClientRect().top - getScrollOffset();

        if (Math.abs(delta) > 2 && attempt < 3) {
            window.scrollBy({ top: delta, behavior: "auto" });
            clickTimeoutRef.current = setTimeout(
                () => settleScroll(node, attempt + 1),
                120
            );
            return;
        }

        isClickScrolling.current = false;
    }, [getScrollOffset]);

    const handleTabClick = (id) => {
        const node = sectionRefs.current[id];
        if (!node) return;

        isClickScrolling.current = true;
        setActiveId(id);

        const top = node.getBoundingClientRect().top + window.scrollY - getScrollOffset();
        window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });

        clearTimeout(clickTimeoutRef.current);
        clickTimeoutRef.current = setTimeout(() => settleScroll(node, 0), 600);
    };

    useEffect(() => () => clearTimeout(clickTimeoutRef.current), []);

    useEffect(() => {
        const scroller = mobileTabsRef.current?.firstElementChild;
        const node = mobileTabsRef.current?.querySelector(`[data-tab-id="${activeId}"]`);
        if (!scroller || !node) return;

        const overflow = scroller.scrollWidth - scroller.clientWidth;
        if (overflow <= 1) return;


        const scroller_rect = scroller.getBoundingClientRect();
        const node_rect = node.getBoundingClientRect();
        const delta =
            node_rect.left + node_rect.width / 2 -
            (scroller_rect.left + scroller_rect.width / 2);

        if (Math.abs(delta) > 1) {
            scroller.scrollBy({ left: delta, behavior: "smooth" });
        }
    }, [activeId, sections.length]);

    if (!sections.length) return null;

    return (

        <div
            id="faq"
            className="mt-5 scroll-mt-[calc(var(--header-height,64px)+16px)] lg:mt-4 flex flex-col-reverse gap-5 lg:flex-row">

            <JsonLd data={json_ld} />

            {/* Desktop aside */}
            <aside className="hidden w-full shrink-0 self-start bg-jmt-white lg:sticky lg:top-[calc(var(--header-height)+12px)] lg:block lg:w-[260px]">
                <nav className="flex flex-col items-start gap-4 rounded-md border border-jmt-neutral-200 px-2 py-4">
                    {sections.map((section) => {
                        const isActive = activeId === section.id;
                        return (
                            <button
                                key={section.id}
                                type="button"
                                onClick={() => handleTabClick(section.id)}
                                className={`flex items-center gap-2 text-right text-sm transition-colors lg:text-base ${isActive
                                    ? "text-jmt-primary-800"
                                    : "text-jmt-main hover:text-jmt-primary-800"
                                    }`}
                            >
                                <div className="flex h-6 w-6 items-center justify-center">
                                    <span
                                        className={`h-1 w-1 shrink-0 rounded-full ${isActive ? "bg-jmt-primary-900" : "bg-jmt-neutral-900"
                                            }`}
                                    />
                                </div>
                                <span>{section.title}</span>
                            </button>
                        );
                    })}
                </nav>
            </aside>

            <div className="flex min-w-0 flex-1 flex-col gap-5">

                <div
                    ref={mobileTabsRef}
                    className="sticky top-[calc(var(--header-height)-1px)] z-20 -mx-4 bg-jmt-white px-4 pt-px lg:hidden">
                    <div className="flex gap-2 overflow-x-auto whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        {sections.map((section) => {
                            const isActive = activeId === section.id;
                            return (
                                <button
                                    key={section.id}
                                    type="button"
                                    data-tab-id={section.id}
                                    onClick={() => handleTabClick(section.id)}
                                    className={`shrink-0 rounded-md border px-3 py-2 text-[13px] transition-colors ${isActive
                                        ? "border-jmt-primary-800 text-jmt-primary-800"
                                        : "border-jmt-neutral-200 text-jmt-main"
                                        }`}
                                >
                                    {section.title}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {sections.map((section) => (
                    <section
                        key={section.id}
                        ref={registerSectionRef(section.id)}
                        data-section-id={section.id}
                        className="scroll-mt-24 flex flex-col"
                    >
                        {/* Mobile section title */}
                        <div className="mb-2 flex items-center gap-2 text-jmt-primary-900 lg:hidden">
                            <RawIcon svg={section.iconSvg} size={22} />
                            <span className="font-bold text-base">{section.title}</span>
                        </div>

                        {/* Desktop section title */}
                        <div className="mb-2 hidden items-center gap-2 text-jmt-primary-900 lg:flex">
                            <RawIcon svg={section.iconSvg} size={22} />
                            <span className="font-bold text-base">{section.title}</span>
                        </div>

                        <div className="flex w-full flex-col lg:pr-8">
                            {section.items.map((item) => (
                                <Accordion
                                    key={item.id}
                                    className="w-full border-b border-jmt-neutral-200"
                                    title={
                                        <span className="block w-full py-4 text-right text-sm font-bold text-jmt-main lg:text-base">
                                            {item.question}
                                        </span>
                                    }
                                    isOpen={openItemId === item.id}
                                    onToggle={(next) => setOpenItemId(next ? item.id : null)}
                                    maxHeight="500px"
                                    headerClassName="flex w-full items-center justify-between gap-4"
                                >
                                    <div className="pb-4 text-right text-sm leading-7 text-jmt-neutral-600 [&_p]:m-0">
                                        <div
                                            dangerouslySetInnerHTML={{ __html: item.answerHtml }}
                                        />
                                    </div>
                                </Accordion>
                            ))}
                        </div>
                    </section>
                ))}

                {/* باکس ارتباط با کارشناسان در انتهای محتوا */}
                <div className="flex items-center justify-center gap-6 rounded-md bg-jmt-neutral-100 p-4">
                    <div className="flex items-center gap-2 text-jmt-main">
                        <Headset className="h-5 w-5 shrink-0 lg:h-6 lg:w-6" />
                        <span className="text-xs font-bold lg:text-sm">
                            سوال شما وجود نداشت؟ از کارشناسان ما بپرسید.
                        </span>
                    </div>

                    <Button
                        href="/contact-us"
                        color_type="primary"
                        text="تماس با ما"
                        end_icon={<ChevronLeft size={16} />}
                        className="shrink-0"
                    />
                </div>
            </div>
        </div>
    );
}
